import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  CTA_PROMOTION,
  LUXPLUS_PROMO_CARD_TYPES,
  LUXPLUS_PROMO_CARD_TYPE_LABELS,
  PROMO_CODE_PROMOTION,
} from '~/utils/luxPlusPayloadBuilder/constants';
import { createDefaultPromoCard } from '~/utils/luxPlusPayloadBuilder/promoCardCarousel/defaults';
import type { LuxPlusPromoCard, LuxPlusPromoCardType } from '~/utils/luxPlusPayloadBuilder/promoCardCarousel/types';
import { formatPromoTitle, getNextPromoDraftLabel, movePromo } from '~/utils/luxPlusPayloadBuilder/promos';

import {
  PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH,
  PAYLOAD_BUILDER_TITLE_WARNING_LENGTH,
  PayloadBuilderTextField,
  getPayloadBuilderCopyLengthWarningProps,
} from '../../FormFields';
import PayloadBuilderRegionChipsField from '../../RegionChipsField';
import { SectionCard } from '../../SectionCard';

interface PromoCardCarouselCardsSectionProps {
  promos: Array<LuxPlusPromoCard>;
  onChange: (promos: Array<LuxPlusPromoCard>) => void;
}

function updatePromo(
  promos: Array<LuxPlusPromoCard>,
  index: number,
  patch: Partial<LuxPlusPromoCard>,
): Array<LuxPlusPromoCard> {
  return promos.map((promo, promoIndex) => (promoIndex === index ? { ...promo, ...patch } : promo));
}

interface PromoCardEditorProps {
  promo: LuxPlusPromoCard;
  index: number;
  promos: Array<LuxPlusPromoCard>;
  onChange: (promos: Array<LuxPlusPromoCard>) => void;
}

function PromoCardEditor({ promo, index, promos, onChange }: PromoCardEditorProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatPromoTitle(promo.draftLabel, promo.title);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={LUXPLUS_PROMO_CARD_TYPE_LABELS[promo.type]} size="small" color="primary" variant="outlined" />
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move promo up">
            <Box component="span">
              <IconButton
                aria-label={`Move promo ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(movePromo(promos, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move promo down">
            <Box component="span">
              <IconButton
                aria-label={`Move promo ${index + 1} down`}
                disabled={index === promos.length - 1}
                onClick={() => onChange(movePromo(promos, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Remove promo">
            <IconButton
              aria-label={`Remove promo ${index + 1}`}
              onClick={() => onChange(promos.filter((_, promoIndex) => promoIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                select
                label="Type"
                value={promo.type}
                onChange={(event) =>
                  onChange(updatePromo(promos, index, { type: event.target.value as LuxPlusPromoCardType }))
                }
                required
              >
                {LUXPLUS_PROMO_CARD_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {LUXPLUS_PROMO_CARD_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </PayloadBuilderTextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Expires at"
                type="datetime-local"
                value={promo.expiresAt}
                onChange={(event) => onChange(updatePromo(promos, index, { expiresAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional. Configured in Australia/Sydney. Leave empty for no expiry."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label="Eligible Regions"
                helperText="Customer portal only shows this card in selected regions. At least one region is required."
                regions={promo.eligibleRegions}
                onChange={(eligibleRegions) => onChange(updatePromo(promos, index, { eligibleRegions }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Label"
                value={promo.label}
                onChange={(event) => onChange(updatePromo(promos, index, { label: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Title"
                value={promo.title}
                onChange={(event) => onChange(updatePromo(promos, index, { title: event.target.value }))}
                required
                {...getPayloadBuilderCopyLengthWarningProps(promo.title, PAYLOAD_BUILDER_TITLE_WARNING_LENGTH)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label="Subtitle"
                value={promo.subtitle}
                onChange={(event) => onChange(updatePromo(promos, index, { subtitle: event.target.value }))}
                required
                {...getPayloadBuilderCopyLengthWarningProps(promo.subtitle, PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH)}
              />
            </Grid>
            {promo.type === PROMO_CODE_PROMOTION ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label="Promo code"
                    value={promo.promoCode}
                    onChange={(event) => onChange(updatePromo(promos, index, { promoCode: event.target.value }))}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <PayloadBuilderTextField
                    label="Terms and conditions"
                    value={promo.termsAndConditions}
                    onChange={(event) =>
                      onChange(updatePromo(promos, index, { termsAndConditions: event.target.value }))
                    }
                    required
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label="Primary action text"
                    value={promo.primaryActionText}
                    onChange={(event) =>
                      onChange(updatePromo(promos, index, { primaryActionText: event.target.value }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label="Primary action URL"
                    value={promo.primaryActionHref}
                    onChange={(event) =>
                      onChange(updatePromo(promos, index, { primaryActionHref: event.target.value }))
                    }
                    helperText="Optional. Use an https:// URL or an internal route starting with /"
                  />
                </Grid>
              </>
            ) : null}
            {promo.type === CTA_PROMOTION ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label="CTA text"
                    value={promo.ctaText}
                    onChange={(event) => onChange(updatePromo(promos, index, { ctaText: event.target.value }))}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PayloadBuilderTextField
                    label="CTA URL"
                    value={promo.ctaLink}
                    onChange={(event) => onChange(updatePromo(promos, index, { ctaLink: event.target.value }))}
                    helperText="Use an https:// URL or an internal route starting with /"
                    required
                  />
                </Grid>
              </>
            ) : null}
            {promo.type !== PROMO_CODE_PROMOTION && promo.type !== CTA_PROMOTION ? (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Flight voucher cards only need the shared label, title, subtitle, regions, and optional expiry.
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function PromoCardCarouselCardsSection({ promos, onChange }: PromoCardCarouselCardsSectionProps) {
  return (
    <SectionCard title="Promo cards" subtitle="Cards shown on the LuxPlus+ member account page." count={promos.length}>
      <Stack spacing={2}>
        {promos.map((promo, index) => (
          <PromoCardEditor key={promo.draftId} promo={promo} index={index} promos={promos} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...promos, createDefaultPromoCard(getNextPromoDraftLabel(promos))])}
        >
          Add promo card
        </Button>
      </Stack>
    </SectionCard>
  );
}
